import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useVacancyStore } from '../../store/vacancyStore';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { optimizedApiService } from '../../services/optimizedApiService';
import { PositionStatusEnum } from '../../../generated-src/client/positions/api';
import type { Position as ApiPosition } from '../../../generated-src/client/positions/api';
import { getPositionStatusDotClass } from '../../utils/vacancyStatus';
import { ReactComponent as TagIcon } from '../../assets/icons/tag-icon.svg';

interface VacancySearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type StatusFilter = 'all' | PositionStatusEnum;

const STATUS_COLORS: Record<PositionStatusEnum, string> = {
  [PositionStatusEnum.ACTIVE]: 'bg-[#00BC7D]',
  [PositionStatusEnum.PAUSED]: 'bg-[#F0B100]',
  [PositionStatusEnum.ARCHIVED]: 'bg-[#DF1C41]',
};

const STATUS_LABELS: Record<PositionStatusEnum, string> = {
  [PositionStatusEnum.ACTIVE]: 'Активные',
  [PositionStatusEnum.PAUSED]: 'Пауза',
  [PositionStatusEnum.ARCHIVED]: 'Архив',
};

export function VacancySearchModal({ isOpen, onClose }: VacancySearchModalProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { setSelectedVacancyById } = useVacancyStore();
  const { isRecruiter } = useRoleAccess();

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [vacancies, setVacancies] = useState<ApiPosition[]>([]);
  const [loading, setLoading] = useState(false);

  const loadVacancies = useCallback(async () => {
    setLoading(true);
    try {
      const ownerFilter = isRecruiter ? 'me' : 'all';
      
      const data = await optimizedApiService.getVacanciesPageData({
        page: 0,
        size: 500,
        owner: ownerFilter,
      });

      setVacancies(data.positions);
    } catch (error) {
      console.error('Error loading vacancies for search:', error);
      setVacancies([]);
    } finally {
      setLoading(false);
    }
  }, [isRecruiter]);

  useEffect(() => {
    if (isOpen) {
      setVacancies([]);
      setLoading(true);
      loadVacancies();
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setSearchValue('');
      setStatusFilter('all');
      setVacancies([]);
    }
  }, [isOpen, loadVacancies]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleVacancyClick = (vacancy: ApiPosition) => {
    setSelectedVacancyById(vacancy.id);
    navigate(`/recruiter/vacancy/${vacancy.id}`);
    onClose();
  };

  const filteredVacancies = useMemo(() => {
    let result = vacancies;

    if (statusFilter !== 'all') {
      result = result.filter(v => v.status === statusFilter);
    }

    if (searchValue.trim()) {
      const search = searchValue.toLowerCase().trim();
      result = result.filter(v => {
        const title = v.title?.toLowerCase() || '';
        const internalTag = ((v as any).internalTag || '').toLowerCase();
        return title.includes(search) || internalTag.includes(search);
      });
    }

    return result;
  }, [vacancies, searchValue, statusFilter]);

  const groupedVacancies = useMemo(() => {
    const groups: Record<PositionStatusEnum, ApiPosition[]> = {
      [PositionStatusEnum.ACTIVE]: [],
      [PositionStatusEnum.PAUSED]: [],
      [PositionStatusEnum.ARCHIVED]: [],
    };

    filteredVacancies.forEach(v => {
      if (v.status && groups[v.status as PositionStatusEnum]) {
        groups[v.status as PositionStatusEnum].push(v);
      }
    });

    return groups;
  }, [filteredVacancies]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex justify-center px-4"
      style={{ paddingTop: '80px' }}
      onKeyDown={handleKeyDown}
    >
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-sm" 
        onClick={onClose}
        aria-hidden
      />
      
      <div 
        className="relative bg-white rounded-2xl w-full max-w-[672px] h-fit max-h-[calc(100vh-120px)] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-3 p-4 pb-1">
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Поиск по вакансиям..."
              className="w-full pl-12 pr-12 py-3 rounded-[14px] bg-[#F9FAFB] text-base text-[var(--text-dark)] placeholder-[#99A1AF] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] transition-all"
            />
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-[#99A1AF]" />
            <button
              onClick={onClose}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-200 rounded-[10px] transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-[#6A7282]" />
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-[10px] text-sm font-medium transition-colors ${
                statusFilter === 'all'
                  ? 'bg-[#E4735C] text-white'
                  : 'bg-[#F3F4F6] text-[#4A5565] hover:bg-[#E5E7EB]'
              }`}
            >
              Все
            </button>
            {Object.entries(STATUS_LABELS).map(([status, label]) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status as PositionStatusEnum)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-[10px] text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-[#E4735C] text-white'
                    : 'bg-[#F3F4F6] text-[#4A5565] hover:bg-[#E5E7EB]'
                }`}
              >
                {statusFilter !== status && (
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS_COLORS[status as PositionStatusEnum]}`} />
                )}
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-y-auto px-4 pb-4">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-gray-500">Загрузка...</div>
            </div>
          ) : filteredVacancies.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-gray-500">Ничего не найдено</div>
            </div>
          ) : statusFilter === 'all' ? (
            <div className="flex flex-col">
              {([PositionStatusEnum.ACTIVE, PositionStatusEnum.PAUSED, PositionStatusEnum.ARCHIVED] as PositionStatusEnum[]).map((status) => {
                const items = groupedVacancies[status];
                if (items.length === 0) return null;
                
                return (
                  <div key={status} className="py-3">
                    <div className="px-3 mb-1">
                      <span className="text-xs font-medium text-[#6A7282]">
                        {STATUS_LABELS[status]}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {items.map((vacancy) => (
                        <VacancySearchItem
                          key={vacancy.id}
                          vacancy={vacancy}
                          onClick={() => handleVacancyClick(vacancy)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col gap-0.5 pt-3">
              {filteredVacancies.map((vacancy) => (
                <VacancySearchItem
                  key={vacancy.id}
                  vacancy={vacancy}
                  onClick={() => handleVacancyClick(vacancy)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface VacancySearchItemProps {
  vacancy: ApiPosition;
  onClick: () => void;
}

function VacancySearchItem({ vacancy, onClick }: VacancySearchItemProps) {
  const internalTag = (vacancy as any).internalTag;

  return (
    <div
      onClick={onClick}
      className="flex items-center gap-3 px-3 py-2 rounded-[10px] cursor-pointer hover:bg-[#F6F8FA] transition-colors"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
    >
      <div
        className={`w-2 h-2 rounded-full flex-shrink-0 ${getPositionStatusDotClass(vacancy.status)}`}
      />
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span className="text-sm font-medium text-[#101828] truncate">
          {vacancy.title}
        </span>
        {internalTag && (
          <div className="flex items-center gap-1">
            <TagIcon className="w-3 h-3 flex-shrink-0 text-[#4A5565]" />
            <span className="text-xs text-[#4A5565] truncate">
              {internalTag}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default VacancySearchModal;
